import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import { strFromU8, strToU8, unzipSync, zipSync } from 'fflate';
import { createId } from '../lib/ids';
import { CURRENT_PROJECT_SCHEMA, migrateProject, type RealImagerProject } from '../types/project';
import type Database from '@tauri-apps/plugin-sql';

interface RealImagerDb extends DBSchema {
  projects: {
    key: string;
    value: RealImagerProject;
    indexes: { 'by-updated': string };
  };
  assets: {
    key: string;
    value: Blob;
  };
  recovery: {
    key: string;
    value: RealImagerProject;
  };
}

export interface ProjectStore {
  list(): Promise<RealImagerProject[]>;
  open(id: string): Promise<RealImagerProject | undefined>;
  save(project: RealImagerProject): Promise<void>;
  saveRecovery(project: RealImagerProject): Promise<void>;
  recover(id: string): Promise<RealImagerProject | undefined>;
  remove(id: string): Promise<void>;
  putAsset(key: string, blob: Blob): Promise<void>;
  getAsset(key: string): Promise<Blob | undefined>;
  exportArchive(project: RealImagerProject): Promise<Blob>;
  importArchive(file: File): Promise<RealImagerProject>;
}

let databasePromise: Promise<IDBPDatabase<RealImagerDb>> | null = null;

function database(): Promise<IDBPDatabase<RealImagerDb>> {
  databasePromise ??= openDB<RealImagerDb>('realimager', 1, {
    upgrade(db) {
      const projects = db.createObjectStore('projects', { keyPath: 'id' });
      projects.createIndex('by-updated', 'updatedAt');
      db.createObjectStore('assets');
      db.createObjectStore('recovery', { keyPath: 'id' });
    }
  });
  return databasePromise;
}

export class BrowserProjectStore implements ProjectStore {
  async list(): Promise<RealImagerProject[]> {
    const db = await database();
    const projects = await db.getAllFromIndex('projects', 'by-updated');
    return projects.map((project) => migrateProject(project)).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  }

  async open(id: string): Promise<RealImagerProject | undefined> {
    const project = await (await database()).get('projects', id);
    return project ? migrateProject(project) : undefined;
  }

  async save(project: RealImagerProject): Promise<void> {
    const db = await database();
    const transaction = db.transaction(['projects', 'recovery'], 'readwrite');
    await transaction.objectStore('projects').put(project);
    await transaction.objectStore('recovery').delete(project.id);
    await transaction.done;
  }

  async saveRecovery(project: RealImagerProject): Promise<void> {
    await (await database()).put('recovery', project);
  }

  async recover(id: string): Promise<RealImagerProject | undefined> {
    const project = await (await database()).get('recovery', id);
    return project ? migrateProject(project) : undefined;
  }

  async remove(id: string): Promise<void> {
    const db = await database();
    const project = await db.get('projects', id);
    if (project) await Promise.all(project.assets.map((asset) => deleteOpfsAsset(asset.storageKey)));
    const transaction = db.transaction(['projects', 'assets', 'recovery'], 'readwrite');
    if (project) {
      await Promise.all(project.assets.map((asset) => transaction.objectStore('assets').delete(asset.storageKey)));
    }
    await transaction.objectStore('projects').delete(id);
    await transaction.objectStore('recovery').delete(id);
    await transaction.done;
  }

  async putAsset(key: string, blob: Blob): Promise<void> {
    if (await writeOpfsAsset(key, blob)) {
      await (await database()).delete('assets', key);
      return;
    }
    await (await database()).put('assets', blob, key);
  }

  async getAsset(key: string): Promise<Blob | undefined> {
    return (await readOpfsAsset(key)) ?? (await database()).get('assets', key);
  }

  async exportArchive(project: RealImagerProject): Promise<Blob> {
    const files: Record<string, Uint8Array> = {
      'project.json': strToU8(JSON.stringify(project, null, 2))
    };
    for (const asset of project.assets) {
      const blob = await this.getAsset(asset.storageKey);
      if (blob) files[`assets/${asset.id}-${safeFilename(asset.name)}`] = new Uint8Array(await blob.arrayBuffer());
    }
    const archive = zipSync(files, { level: 6 });
    return new Blob([archive.buffer as ArrayBuffer], { type: 'application/x-realimager-project' });
  }

  async importArchive(file: File): Promise<RealImagerProject> {
    const entries = unzipSync(new Uint8Array(await file.arrayBuffer()));
    const manifest = entries['project.json'];
    if (!manifest) throw new Error('The archive does not contain project.json.');
    let project = migrateProject(JSON.parse(strFromU8(manifest)));
    if (project.schemaVersion !== CURRENT_PROJECT_SCHEMA) throw new Error('Unsupported project version.');

    if (await this.open(project.id)) {
      const nextId = createId('project');
      project = {
        ...project,
        id: nextId,
        name: `${project.name} imported`,
        assets: project.assets.map((asset) => ({ ...asset, storageKey: `${nextId}/${asset.id}` }))
      };
    }

    for (const asset of project.assets) {
      const path = Object.keys(entries).find((key) => key.startsWith(`assets/${asset.id}-`));
      if (!path) throw new Error(`The project is missing ${asset.name}.`);
      await this.putAsset(asset.storageKey, new Blob([entries[path].buffer as ArrayBuffer], { type: asset.mimeType }));
    }
    const imported = { ...project, updatedAt: new Date().toISOString() };
    await this.save(imported);
    return imported;
  }
}

let nativeDatabasePromise: Promise<Database> | null = null;

async function nativeDatabase(): Promise<Database> {
  nativeDatabasePromise ??= import('@tauri-apps/plugin-sql').then(async ({ default: Database }) => {
    const db = await Database.load('sqlite:realimager.db');
    await db.execute(`CREATE TABLE IF NOT EXISTS projects (
      id TEXT PRIMARY KEY NOT NULL,
      updated_at TEXT NOT NULL,
      document TEXT NOT NULL
    )`);
    await db.execute(`CREATE TABLE IF NOT EXISTS recovery (
      id TEXT PRIMARY KEY NOT NULL,
      document TEXT NOT NULL
    )`);
    return db;
  });
  return nativeDatabasePromise;
}

interface ProjectRow {
  document: string;
}

export class NativeProjectStore extends BrowserProjectStore {
  override async list(): Promise<RealImagerProject[]> {
    const rows = await (await nativeDatabase()).select<ProjectRow[]>(
      'SELECT document FROM projects ORDER BY updated_at DESC'
    );
    return rows.map((row) => migrateProject(JSON.parse(row.document)));
  }

  override async open(id: string): Promise<RealImagerProject | undefined> {
    const rows = await (await nativeDatabase()).select<ProjectRow[]>(
      'SELECT document FROM projects WHERE id = $1 LIMIT 1',
      [id]
    );
    return rows[0] ? migrateProject(JSON.parse(rows[0].document)) : undefined;
  }

  override async save(project: RealImagerProject): Promise<void> {
    const db = await nativeDatabase();
    await db.execute(
      `INSERT INTO projects (id, updated_at, document) VALUES ($1, $2, $3)
       ON CONFLICT(id) DO UPDATE SET updated_at = excluded.updated_at, document = excluded.document`,
      [project.id, project.updatedAt, JSON.stringify(project)]
    );
    await db.execute('DELETE FROM recovery WHERE id = $1', [project.id]);
  }

  override async saveRecovery(project: RealImagerProject): Promise<void> {
    await (await nativeDatabase()).execute(
      `INSERT INTO recovery (id, document) VALUES ($1, $2)
       ON CONFLICT(id) DO UPDATE SET document = excluded.document`,
      [project.id, JSON.stringify(project)]
    );
  }

  override async recover(id: string): Promise<RealImagerProject | undefined> {
    const rows = await (await nativeDatabase()).select<ProjectRow[]>(
      'SELECT document FROM recovery WHERE id = $1 LIMIT 1',
      [id]
    );
    return rows[0] ? migrateProject(JSON.parse(rows[0].document)) : undefined;
  }

  override async remove(id: string): Promise<void> {
    const project = await this.open(id);
    if (project) await Promise.all(project.assets.map((asset) => removeNativeAsset(asset.storageKey)));
    const db = await nativeDatabase();
    await db.execute('DELETE FROM projects WHERE id = $1', [id]);
    await db.execute('DELETE FROM recovery WHERE id = $1', [id]);
  }

  override async putAsset(key: string, blob: Blob): Promise<void> {
    const { BaseDirectory, mkdir, writeFile } = await import('@tauri-apps/plugin-fs');
    await mkdir('realimager-assets', { baseDir: BaseDirectory.AppData, recursive: true });
    await writeFile(nativeAssetPath(key), new Uint8Array(await blob.arrayBuffer()), {
      baseDir: BaseDirectory.AppData
    });
  }

  override async getAsset(key: string): Promise<Blob | undefined> {
    try {
      const { BaseDirectory, readFile } = await import('@tauri-apps/plugin-fs');
      const bytes = await readFile(nativeAssetPath(key), { baseDir: BaseDirectory.AppData });
      return new Blob([bytes.buffer as ArrayBuffer]);
    } catch {
      return undefined;
    }
  }
}

function nativeAssetPath(key: string): string {
  return `realimager-assets/${opfsFileName(key)}`;
}

async function removeNativeAsset(key: string): Promise<void> {
  try {
    const { BaseDirectory, remove } = await import('@tauri-apps/plugin-fs');
    await remove(nativeAssetPath(key), { baseDir: BaseDirectory.AppData });
  } catch {
    // A missing managed asset is already removed.
  }
}

function safeFilename(name: string): string {
  return name.replace(/[^a-zA-Z0-9._-]+/g, '_');
}

const nativeRuntime = typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window;
export const projectStore: ProjectStore = nativeRuntime ? new NativeProjectStore() : new BrowserProjectStore();

export async function requestPersistentStorage(): Promise<boolean> {
  if (!navigator.storage?.persist) return false;
  return navigator.storage.persist();
}

type StorageManagerWithOpfs = StorageManager & { getDirectory?: () => Promise<FileSystemDirectoryHandle> };

function opfsFileName(key: string): string {
  return `${key.replace(/[^a-zA-Z0-9._-]+/g, '_')}.asset`;
}

async function opfsRoot(): Promise<FileSystemDirectoryHandle | null> {
  try {
    const storage = navigator.storage as StorageManagerWithOpfs;
    return storage.getDirectory ? await storage.getDirectory() : null;
  } catch {
    return null;
  }
}

async function writeOpfsAsset(key: string, blob: Blob): Promise<boolean> {
  try {
    const root = await opfsRoot();
    if (!root) return false;
    const assets = await root.getDirectoryHandle('assets', { create: true });
    const handle = await assets.getFileHandle(opfsFileName(key), { create: true });
    const writable = await handle.createWritable();
    await writable.write(blob);
    await writable.close();
    return true;
  } catch {
    return false;
  }
}

async function readOpfsAsset(key: string): Promise<Blob | undefined> {
  try {
    const root = await opfsRoot();
    if (!root) return undefined;
    const assets = await root.getDirectoryHandle('assets');
    return (await assets.getFileHandle(opfsFileName(key))).getFile();
  } catch {
    return undefined;
  }
}

async function deleteOpfsAsset(key: string): Promise<void> {
  try {
    const root = await opfsRoot();
    const assets = await root?.getDirectoryHandle('assets');
    await assets?.removeEntry(opfsFileName(key));
  } catch {
    // Missing OPFS entries are already deleted.
  }
}
